import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, set as vueSet } from 'vue';
import StatisticsContent from '@/modules/Statistics/components/StatisticsContent.vue';
import StatisticsSidebar from '@/modules/Statistics/components/StatisticsSidebar.vue';
import store from '@/store';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { fetchChatProjects as fetchChatProjectsHelper } from '@/helpers/chat/ChatHelper';
import { UserRepository } from '@/repositories/UserRepository';
import DashboardRepository from '@/modules/Statistics/api/repositories/DashboardRepository';
import DashboardReportRepository from '@/modules/Statistics/api/repositories/DashboardReportRepository';
import ProjectRepository from '@/repositories/ProjectRepository';
import { fetchActivitiesProjects } from '@/helpers/TableHelper';
import { ProjectTypes } from '@/enums/models/ProjectTypes';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import Dashboard from '@/modules/Statistics/api/models/Dashboard';
import DashboardReport from '@/modules/Statistics/api/models/DashboardReport';
import Report from '@/modules/Statistics/api/models/Report';
import { DashboardReportFields } from '@/modules/Statistics/interfaces/DashboardReport';
import { DashboardReportData } from '@/modules/Statistics/interfaces/DashboardReportData';
import { ChatMessage } from '@/interfaces/components/chat/ChatMessage';
import IntroScreen from '@/models/IntroScreen';
import CMSUser from '@/models/CMSUser';
import News from '@/modules/Statistics/CMS/api/models/News';
import { ReportTimeFrame } from '@/modules/Statistics/interfaces/ReportTimeFrame';
import { AxiosResponse } from 'axios';
import CMSUserRepository from '@/repositories/CMSUserRepository';
import { serializeNewActivities, serializeNewComments } from '@/modules/Statistics/helpers/StatisticsDashboardHelper';
import { useDashboardDragAndDrop } from '@/modules/Statistics/composables/UseDashboardDragAndDrop';


export default /*#__PURE__*/_defineComponent({
  __name: 'StatisticsDashboard',
  setup(__props) {

const { proxy } = getCurrentInstance()!;

const nextSteps = computed<any[]>(() => store.getters['tableData/nextSteps']);

const isExistingDashboard = ref<boolean>(false);
const isEditModeOn = ref<boolean>(false);
const loadingOverlay = new LoadingOverlayHelper(proxy as any, {});
const isLoading = ref<boolean>(false);
const currentUserId = ref<string>('1');
const filteredUserName = ref<string>('');
const dashboardId = ref<string>('');
const dashboardReports = ref<DashboardReportFields[]>([]);
const timeFrame = ref<ReportTimeFrame>({
    year: new Date().getFullYear(),
    week: 'Current',
    month: '/',
});
const expandModeForPrinting = ref<boolean>(false);
const isPrinting = ref<boolean>(false);
const tablePagination = ref({ paginationSize: 5, activePrintingReport: '' });

/* ============================================================
 * LOCALIZED DISPLAY STATE
 * ============================================================ */
const timeFrameNext = computed<ReportTimeFrame>(() => {
    return {
        ...timeFrame.value,
        week: proxy?.$t(timeFrame.value.week as string) as string,
        month: proxy?.$t(timeFrame.value.month as string) as string,
    };
});

const sidebarData = ref<{ newTasks: any[]; newComments: ChatMessage[] }>({
    newTasks: [],
    newComments: [],
});

/* ============================================================
 * SIDEBAR ACTIVITY
 * ============================================================ */
const searchOptions = ref({
    page: 1,
    size: 10,
    query: '',
    total: 0,
});
const activeTabKey = ref(1);
const newsReports = ref<any[]>([]);

const chatProjects = computed(() => {
    return ProjectRepository.getChatProjects();
});

const position = computed(() => {
    return dashboardReports.value.length;
});

const currentUser = computed(() => {
    return UserRepository.getCurrentUser();
});

function editDashboard() {
    isEditModeOn.value = !isEditModeOn.value;
}

const { startDrag, onDrop } = useDashboardDragAndDrop(dashboardReports, isEditModeOn);

/* ============================================================
 * PRINTING
 * ============================================================ */

function setPrintingStateToActive() {
    isPrinting.value = true;
}

function setPrintingStateToFalse() {
    isPrinting.value = false;
}

function expandTableForPrinting(reportId: string) {
    tablePagination.value.activePrintingReport = reportId;
    setPrintingStateToActive();
}

function revertTableToNormalSize() {
    tablePagination.value.activePrintingReport = '';
    setPrintingStateToFalse();
}

async function prepareTablesForPrinting() {
    setPrintingStateToActive();
    await nextTick();

    // @ts-ignore
    await (proxy as any).$htmlToPaper('dashboard-wrapper', {
        windowTitle: proxy?.$t('Roltek dashboard'),
    });

    await nextTick();
    setPrintingStateToFalse();
}

async function printDashboard() {
    await prepareTablesForPrinting();
}

/* ============================================================
 * SERIALIZATION
 * ============================================================ */

async function serializeTaskbarData() {
    // Serialize latest activities
    serializeNewActivities(nextSteps.value, sidebarData.value);

    // Serialize latest chat messages
    serializeNewComments(chatProjects.value, sidebarData.value);
}

/* ============================================================
 * DATA FETCHING
 * ============================================================ */

// Sidebar activity fetching
function fetchChatProjects() {
    if (currentUserId.value == null) {
        return Promise.reject();
    }

    try {
        fetchChatProjectsHelper({
            activeTabKey: activeTabKey.value,
            userId: currentUserId.value,
            searchOptions: searchOptions.value,
        });
    } catch (error) {
        isLoading.value = false;
        return Promise.reject(error);
    }

    return Promise.resolve();
}

async function fetchIndividualReportData() {
    if (dashboardReports.value.length) {
        // Get report data for each report using promise all in parallel
        const promises: Array<Promise<{ status: string; value: DashboardReportData }>> = [];
        let fetchedReports: Array<
            PromiseSettledResult<{
                status: string;
                value: DashboardReportData;
            }>
        > = [];

        dashboardReports.value.map((report: any) => {
            promises.push(
                DashboardReport.getReport(report.id, timeFrameNext.value, currentUserId.value).then((res) => ({
                    ...res.data,
                    id: report.id,
                    shouldExpandTable: false,
                })),
            );
        });

        try {
            fetchedReports = await Promise.allSettled(promises);
        } catch (e) {
            (proxy as any).$notification.error({
                message: proxy?.$t('Error fetching reportData!'),
                description: proxy?.$t(e as string),
            });
            isLoading.value = false;
            return;
        }

        if (fetchedReports.length) {
            // Replace report data field with fetched data
            fetchedReports.map((report: any) => {
                if (report.status === 'fulfilled') {
                    const index = dashboardReports.value.findIndex(
                        (fetchedReportData: any) => fetchedReportData.id === report.value.id,
                    );
                    dashboardReports.value[index].statisticReport.data = { ...report.value };
                }
            });
        }
    }

    return Promise.resolve();
}

async function fetchDashboardData() {
    try {
        if (dashboardId.value) {
            const dashboard = await Dashboard.getById(dashboardId.value);
            dashboardReports.value = dashboard.data.dashboardReports.slice();

            isExistingDashboard.value = true;
        } else {
            const response = await Dashboard.getCurrent();

            if (response?.data) {
                dashboardReports.value = response.data.dashboardReports.slice();
                dashboardId.value = String(response.data.id);
            }
        }

        // Fetch report data individually after fetching all reports
        await fetchIndividualReportData();
    } catch (e) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Error fetching dashboard!'),
            description: proxy?.$t(e as string),
        });
        isLoading.value = false;
        return;
    }

    return Promise.resolve();
}

function checkIfReportIsInDashboard(reportId: string) {
    // Check if report id is already in dashboard reports: statisticReport and throw error if it is
    const isReportInDashboard = dashboardReports.value.some((dashboardReport: DashboardReportFields) => {
        return dashboardReport.statisticReport.id === reportId;
    });

    return isReportInDashboard;
}

async function addNewReportAndFetchDataForIt(fetchedReport: AxiosResponse<DashboardReportFields>) {
    let reportData: AxiosResponse<DashboardReportData>;
    dashboardReports.value.push(fetchedReport.data);

    try {
        reportData = await DashboardReport.getReport(fetchedReport.data.id, timeFrameNext.value, currentUserId.value);
    } catch (e) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Error fetching report data!'),
            description: '',
        });
        return;
    } finally {
        isLoading.value = false;
    }

    // Add report data to report in dashboard reports dinamically
    const index = dashboardReports.value.findIndex((report: any) => report.id === fetchedReport.data.id);
    vueSet(dashboardReports.value[index].statisticReport, 'data', { ...reportData.data });
}

// Fetching a new report from BE statistics report and adding it to dashboard reports
async function addNewReport(report: DashboardReportFields, size: 'S' | 'M' | 'L') {
    let fetchedReport;
    isLoading.value = true;

    const isReportInDashboard = checkIfReportIsInDashboard(report.id);

    if (isReportInDashboard) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Report already in dashboard!'),
            description: '',
        });
        isLoading.value = false;
        return;
    }

    try {
        fetchedReport = await DashboardReport.createNewReport({
            report,
            size,
            position: position.value,
            dashboardId: dashboardId.value,
        });
    } catch (e) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Error creating report!'),
            description: '',
        });

        isLoading.value = false;
        return;
    }

    addNewReportAndFetchDataForIt(fetchedReport);
}

function deleteChart(id: string) {
    isLoading.value = true;

    // Delete chart locally
    const index = dashboardReports.value.findIndex((report: any) => report.id === id);
    dashboardReports.value.splice(index, 1);

    // Delete chart from database
    try {
        DashboardReport.deleteExistingReport(id);
    } catch (e) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Error deleting report!'),
            description: proxy?.$t(e as string),
        });
        return;
    } finally {
        isLoading.value = false;
    }

    (proxy as any).$notification.success({
        message: proxy?.$t('Report deleted successfully!'),
        description: '',
    });
}

async function saveDashboard() {
    isLoading.value = true;

    try {
        // Set correct positions for all reports
        dashboardReports.value.forEach((report: any, index: number) => {
            report.position = index;

            DashboardReport.updateExistingReport(report, dashboardId.value);
        });
    } catch (e) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Error saving dashboard!'),
            description: proxy?.$t(e as string),
        });
        isLoading.value = false;
        return;
    }

    (proxy as any).$notification.success({
        message: proxy?.$t('Dashboard successfully saved'),
        description: '',
    });

    isEditModeOn.value = false;
    isLoading.value = false;
}

function notifyUserOnEditModeToggle() {
    // Remove any existing notifications
    (proxy as any).$notification.destroy();

    if (isEditModeOn.value) {
        (proxy as any).$notification.info({
            message: proxy?.$t('You are now in edit mode'),
            description: proxy?.$t('You can now add, remove, change position or resize reports'),
            duration: 3,
        });
    } else {
        (proxy as any).$notification.info({
            message: proxy?.$t('You are now in view mode'),
            description: proxy?.$t('Your changes have not been saved'),
            duration: 3,
        });
    }
}

function setDashboardPrintOption(data: { reportId: string; shouldExpandTableForPrint: boolean }) {
    const index = dashboardReports.value.findIndex((report: DashboardReportFields) => report.id === data.reportId);

    vueSet(dashboardReports.value[index], 'shouldExpandTable', data.shouldExpandTableForPrint);

    if (!data.shouldExpandTableForPrint) {
        expandModeForPrinting.value = false;
    }
}

function setReportsExpandState() {
    dashboardReports.value.forEach((report: DashboardReportFields) => {
        vueSet(report, 'shouldExpandTable', expandModeForPrinting.value);
    });
}

async function filterDashboardReportsByDateAndUser(userId: string) {
    isLoading.value = true;
    currentUserId.value = userId;
    filteredUserName.value = CMSUserRepository.getNameById(userId);

    timeFrameNext.value.year = String(timeFrameNext.value.year);
    timeFrameNext.value.week = String(timeFrameNext.value.week);
    timeFrameNext.value.month = String(timeFrameNext.value.month);
    try {
        const response = await Dashboard.getByUserId(userId, dashboardId.value);

        if (response?.data) {
            dashboardReports.value = response.data.dashboardReports.slice();
            dashboardId.value = String(response.data.id);
        }

        await fetchIndividualReportData();
    } catch (e) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Error filtering reports!'),
            description: proxy?.$t(e as string),
        });
        return;
    } finally {
        isLoading.value = false;
    }

    (proxy as any).$notification.success({
        message: proxy?.$t('Dashboard reports filtered successfully!'),
        description: '',
    });
}

EventBus.$on(EventBusEvents.deleteChart, deleteChart);
EventBus.$on(EventBusEvents.setDashboardPrintOption, setDashboardPrintOption);

onMounted(async () => {
    isLoading.value = true;
    // DashboardReport.deleteExistingReport('144');

    // Get dashboard id from route params
    const params = (proxy as any).$route.params;
    if (params.dashboardId) {
        dashboardId.value = String(params.dashboardId);
    }

    if (currentUser.value) {
        currentUserId.value = currentUser.value.id;
        filteredUserName.value = currentUser.value.name;
    }

    // Fetch everything in parallel
    try {
        await Promise.allSettled([
            Report.getAll(),
            // Fetch activities and chat projects for taskbar
            // fetchActivitiesProjects(currentUser.value!.id),
            // Get projects overview data
            IntroScreen.getAll(),
            News.getAll(),
            CMSUser.getAll(),
            // Fetch taskbar and dashboard data for a current dashboard
            // fetchChatProjects(),
            fetchDashboardData(),
        ]);
    } catch (e) {
        (proxy as any).$notification.error({
            message: proxy?.$t('Error fetching dashboard!'),
            description: proxy?.$t(e as string),
        });
        return;
    } finally {
        isLoading.value = false;
    }

    // serializeTaskbarData();
});

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.deleteChart, deleteChart);
    EventBus.$off(EventBusEvents.setDashboardPrintOption, setDashboardPrintOption);
});

return { __sfc: true,proxy, nextSteps, isExistingDashboard, isEditModeOn, loadingOverlay, isLoading, currentUserId, filteredUserName, dashboardId, dashboardReports, timeFrame, expandModeForPrinting, isPrinting, tablePagination, timeFrameNext, sidebarData, searchOptions, activeTabKey, newsReports, chatProjects, position, currentUser, editDashboard, startDrag, onDrop, setPrintingStateToActive, setPrintingStateToFalse, expandTableForPrinting, revertTableToNormalSize, prepareTablesForPrinting, printDashboard, serializeTaskbarData, fetchChatProjects, fetchIndividualReportData, fetchDashboardData, checkIfReportIsInDashboard, addNewReportAndFetchDataForIt, addNewReport, deleteChart, saveDashboard, notifyUserOnEditModeToggle, setDashboardPrintOption, setReportsExpandState, filterDashboardReportsByDateAndUser, StatisticsContent, StatisticsSidebar }
}

})